/**
 * Command-palette filter/ranking — pure, DOM-free, unit-tested (test/palette.test.ts). The widget's
 * Ctrl/Cmd+K palette builds its entries (commands, suggestion chips, follow-ups) and hands them here;
 * this module only decides WHICH entries match a query and in WHAT order.
 *
 * Scoring is deliberately simple (no fuzzy-matching dependency): exact > prefix > word-prefix >
 * substring > subsequence, with small tie-breakers for tighter/earlier matches. Anything that is not
 * at least a case-insensitive subsequence of the text is a non-match (score 0) and is filtered out.
 */
/**
 * Score how well `query` matches `text` (case-insensitive). 0 = no match. Higher is better:
 *   exact 1000 · prefix ~900 · word-prefix ~700 · substring ~500 · subsequence ≤300 (gap-penalised).
 */
export function scoreMatch(query, text) {
    const q = query.trim().toLowerCase();
    if (!q)
        return 1; // an empty query matches everything equally
    const t = text.toLowerCase();
    if (!t)
        return 0;
    if (t === q)
        return 1000;
    if (t.startsWith(q))
        return 900 - Math.min(t.length - q.length, 50);
    // Prefix of a later word ("mem" → "Open memory"). Earlier words rank slightly higher.
    const words = t.split(/[\s/_.,:-]+/).filter(Boolean);
    for (let w = 1; w < words.length; w++) {
        if (words[w].startsWith(q))
            return 700 - Math.min(w * 10, 100);
    }
    const at = t.indexOf(q);
    if (at >= 0)
        return 500 - Math.min(at, 100);
    // Subsequence: every query char appears in order; penalise gaps + a late start.
    let from = 0;
    let prev = -1;
    let gaps = 0;
    let first = -1;
    for (const ch of q) {
        const idx = t.indexOf(ch, from);
        if (idx < 0)
            return 0;
        if (prev >= 0)
            gaps += idx - prev - 1;
        else
            first = idx;
        prev = idx;
        from = idx + 1;
    }
    return Math.max(1, 300 - gaps * 8 - Math.min(first, 50));
}
/** Best score for a candidate: its label at full weight, its keywords slightly discounted. */
export function scoreCandidate(query, cand) {
    let best = scoreMatch(query, cand.label);
    for (const k of cand.keywords ?? []) {
        const s = scoreMatch(query, k);
        if (s * 0.9 > best)
            best = Math.round(s * 0.9);
    }
    return best;
}
/**
 * Filter + rank palette entries for a query. Non-matches drop out; ties keep the caller's order
 * (stable), so with an empty query the input order is returned unchanged.
 */
export function rankPalette(items, query) {
    const scored = items
        .map((item, i) => ({ item, i, score: scoreCandidate(query, item) }))
        .filter((s) => s.score > 0);
    scored.sort((a, b) => b.score - a.score || a.i - b.i);
    return scored.map((s) => s.item);
}
//# sourceMappingURL=palette.js.map